import { Button, Card, Col, Form, Input, Row, Select, Space, Typography } from "antd";
import CardChevron from "@/Components/Common/CardChevron";
import { useEffect, useMemo, useState } from "react";
import { countries, formatCallingCode } from "../../Lib/Utils/countries";
import { matchesCountryTerm } from "../../Lib/Utils/countryFilter";
import CountrySearchPopup from "../Common/Inputs/CountrySearchPopup";
import FlagImage from "../Shared/FlagImage";
import "./NeedHelpContactForm.css";
import {
  IContactCategorieslist,
  IContactCategoriesResponse,
  IContactCategoryConfigResponse,
  IContactFormValues,
} from "../../Orchestrator/Types/Interface/ICustomerContact";
import Image from "next/image";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { useIntl } from "react-intl";
import { extractMobileCodeAndNumber } from "../../Lib/Utils/general";
import { MOBILE_CODE_DEFAULT } from "../../config/global";
const { Title } = Typography;
const { Option } = Select;

type NeedHelpContactFormProps = {
  prefillData?: Partial<IContactFormValues>;
  onSubmit: (values: IContactFormValues) => void;
};

export default function NeedHelpContactForm({
  prefillData,
  onSubmit,
}: NeedHelpContactFormProps) {
  const intl = useIntl();
  const [categoriesData] = useRecState<IContactCategoryConfigResponse>(
    "contactCategoriesAtom"
  );
  const [form] = Form.useForm<IContactFormValues>();
  const [isValidForm, setIsValidForm] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const requiredFields: (keyof IContactFormValues)[] = [
    "firstName",
    "lastName",
    "contactEmail",
    "phoneNumber",
    "category",
    "message",
  ];
  const nameRegex = /^[\p{L}\p{M} ]{2,50}$/u;

  useEffect(() => {
    if (prefillData) {
      const { mobileCode, phoneNumber } = extractMobileCodeAndNumber(prefillData.phoneNumber || "");
      form.setFieldsValue({
        firstName: prefillData.firstName || "",
        lastName: prefillData.lastName || "",
        contactEmail: prefillData.contactEmail || "",
        bookingRef: "",
        category: prefillData.category || undefined,
        message: prefillData.message || "",
        mobileCode: mobileCode || MOBILE_CODE_DEFAULT,
        phoneNumber: phoneNumber
      });
    }
  }, [prefillData]);

  const filteredCountries = useMemo(
    () => countries.filter((c) => matchesCountryTerm(c, searchTerm)),
    [searchTerm],
  );

  const handleSubmit = (values: IContactFormValues) => {
    const payload: IContactFormValues = {
      ...values,
      phoneNumber: (values.mobileCode || "") + (values.phoneNumber || ""),
    };
    delete payload.mobileCode;
    onSubmit(payload);
    form.resetFields();
  };

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsValidForm(true))
      .catch(() => setIsValidForm(false));
  }, [form, values]);

  const validateMobile = (mobile: string, countryCode: string) => {
    const country = countries.find((c) => c.countryCallingCode === countryCode);
    if (!country) return false;
    return mobile.length === country.phoneLength;
  };

  return (
    <Card className="contact-form-card">
      <Title level={4} className="mb-4">
        {intl.formatMessage({ id: "contactForm" })}
      </Title>
      <p className="confirmation-message">
        {intl.formatMessage({ id: "senMessage" })}
      </p>

      <Form
        name="contact-form"
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        className="mt-20"
        requiredMark={false}
        validateTrigger="onBlur"
      >
        <Row gutter={[20, 20]} className="row-mobile-view">
          <Col span={12}>
            <Form.Item
              name="firstName"
              label={intl.formatMessage({ id: "firstName" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "contactFirstNameRequired",
                  }),
                },
                {
                  pattern: nameRegex,
                  message: intl.formatMessage({
                    id: "contactOnlyLettersRange",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({ id: "firstNamePlaceholder" })}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="lastName"
              label={intl.formatMessage({ id: "lastName" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "contactLastNameRequired",
                  }),
                },
                {
                  pattern: nameRegex,
                  message: intl.formatMessage({
                    id: "contactOnlyLettersRange",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({ id: "lastNamePlaceholder" })}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="contactEmail"
              label={intl.formatMessage({ id: "emailpersonal" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: "contactEmailRequired" }),
                },
                {
                  type: "email",
                  message: `${intl.formatMessage({ id: "invalidEmail" })}`,
                },
              ]}
            >
              <Input placeholder="name@xyz.com" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={intl.formatMessage({ id: "contactMobileLabel" })} className="mb-0">
              <Space.Compact className="w-100 radius-12" >
                <Form.Item
                  name="mobileCode"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: "invalidMobile" })
                    }
                  ]}
                >
                  <Select
                    className="country-code-select"
                    optionLabelProp="label"
                    styles={{ popup: { root: { width: "320px", maxWidth: "360px" } } }}
                    suffixIcon={
                      <Image
                        src="/assets/contact/caret-down.svg"
                        alt=""
                        height={20}
                        width={20}
                      />
                    }
                    onOpenChange={(open) => {
                      if (!open) setSearchTerm("");
                    }}
                    popupRender={(menu) => (
                      <CountrySearchPopup
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={intl.formatMessage({ id: "searchCountryOrCode" })}
                        inputClassName="country-search-input"
                      >
                        {menu}
                      </CountrySearchPopup>
                    )}
                  >
                    {filteredCountries.map((country) => (
                      <Option
                        key={country.id}
                        value={country.countryCallingCode}
                        label={
                          <div className="country-option d-flex gap-10 align-items-center">
                            <FlagImage countryCode={country.countryCode} />
                            <span>{formatCallingCode(country.countryCallingCode)}</span>
                          </div>
                        }
                        className="country-code-option"
                      >
                        <div className="country-option d-flex gap-10 align-items-center">
                          <FlagImage countryCode={country.countryCode} />
                          <span>{country.label} {formatCallingCode(country.countryCallingCode)}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="phoneNumber"
                  className="phone-number-input"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: "contactMobileRequired" }),
                    },
                    {
                      validator: async (_, value) => {
                        if (!value) return Promise.resolve();
                        const mobile = value.replace(/\D/g, "");
                        const countryCode = form.getFieldValue("mobileCode");
                        if (!validateMobile(mobile, countryCode)) {
                          return Promise.reject(new Error(intl.formatMessage({ id: "accountMobileValidation" })));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="XX XXX XXXX"
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="bookingRef"
              label={`${intl.formatMessage({
                id: "bookingReferenceText",
              })} (${intl.formatMessage({ id: "OPTIONAL" })})`}
              rules={[
                {
                  max: 20,
                  message: intl.formatMessage({ id: "contactRefMaxLength" }),
                },
                {
                  // Optional field — pattern only checks non-empty values.
                  // Booking refs are alphanumeric (e.g. ERLMJR16). Charset +
                  // minimum length only here; the upper bound is handled by
                  // the `max: 20` rule above so a too-long input gets a
                  // length-specific error instead of a generic charset one.
                  // Range left at 20 (vs. real format 6-8) as a defensive
                  // upper bound against future ref-format changes.
                  pattern: /^[A-Z0-9]{6,}$/i,
                  message: intl.formatMessage({ id: "contactRefInvalid" }),
                },
              ]}
            >
              <Input
                maxLength={20}
                placeholder={intl.formatMessage({
                  id: "bookingRefPlaceholder",
                })}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="category"
              label={intl.formatMessage({ id: "categoryText" })}
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select
                suffixIcon={
                  <CardChevron direction="down" width={12} height={7} />
                }
                className="category-select"
                placeholder={intl.formatMessage({ id: "selectCategoryText" })}
              >
                {categoriesData.categories?.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {intl.formatMessage({ id: item.value })}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="message"
              label={intl.formatMessage({ id: "contactHelpTitle" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: "contactMessageRequired" }),
                },
                {
                  min: categoriesData.messageMinLength,
                  message: intl.formatMessage(
                    { id: "contactMessageMin" },
                    { count: categoriesData.messageMinLength }
                  ),
                },
                {
                  max: categoriesData.messageMaxLength,
                  message: intl.formatMessage(
                    {
                      id: "contactMessageMax",
                    },
                    { count: categoriesData.messageMaxLength }
                  ),
                },
              ]}
            >
              <Input.TextArea
                rows={6}
                placeholder={intl.formatMessage({
                  id: "contactMessagePlaceholder",
                })}
              />
            </Form.Item>
          </Col>

          <Col span={24} className="justify-content-end">
            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={!isValidForm}
              className="submit-button"
            >
              {intl.formatMessage({ id: "contactSendMessage" })}
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}
